// routeGeometryHash.js
// Stable, order-sensitive hash of a route's geometry so we can tell when a route
// has changed since its ortho maps were generated. Prefers the road-snapped
// pathData line (what ortho is actually built from); falls back to waypoints.
function coordsFromInput(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  const c = input?.pathData?.geometry?.coordinates;
  if (Array.isArray(c) && c.length >= 2) return c;
  const wp = input?.waypoints;
  if (Array.isArray(wp) && wp.length >= 2) {
    return wp.map((w) => [w.lng, w.lat]);
  }
  return [];
}

export function computeRouteGeometryHash(input) {
  const coords = coordsFromInput(input);
  if (coords.length < 2) return "";
  // Quantize to ~1e-7 deg (~1cm) so trivial float noise doesn't read as a change,
  // then FNV-1a over the string form. Deterministic and dependency-free.
  const str = coords
    .map(([x, y]) => `${Math.round(x * 1e7)},${Math.round(y * 1e7)}`)
    .join(";");
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return `${coords.length}-${h.toString(16)}`;
}

export function isRouteGeometryDirty(path) {
  const stored = path?.orthoGeomHash;
  if (!stored) return false;
  return computeRouteGeometryHash(path) !== stored;
}

function coordsStr(input) {
  return coordsFromInput(input)
    .map(([x, y]) => `${Math.round(x * 1e7)},${Math.round(y * 1e7)}`)
    .join(";");
}

/**
 * Hash of everything saveLasSeparationPathToDrive actually persists — path
 * labels/geometry plus lane labels/geometry/position/direction/speeds — so
 * the Lanes tab can tell whether there's unsaved work since the last click
 * of "Save path". Order-sensitive and quantized like computeRouteGeometryHash.
 */
export function computeSaveableStateHash(paths, lanes) {
  const pathsPart = (Array.isArray(paths) ? paths : [])
    .map((p) => `${p?.id ?? ""}|${p?.label ?? ""}|${coordsStr(p)}`)
    .join("~");
  const lanesPart = (Array.isArray(lanes) ? lanes : [])
    .map((l) => {
      const speedStr = Array.isArray(l?.speedSegments)
        ? l.speedSegments
            .map((s) => `${s?.mileMark ?? ""}:${s?.speedMph ?? ""}`)
            .join(",")
        : "";
      return `${l?.id ?? ""}|${l?.label ?? ""}|${l?.lanePosition ?? ""}|${l?.direction ?? ""}|${coordsStr(l)}|${speedStr}`;
    })
    .join("~");
  const str = `P[${pathsPart}]L[${lanesPart}]`;
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}
